import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik, Form } from 'formik';
import { useApolloClient, useQuery } from '@apollo/client';

import TextInput from '../../../TextInput/TextInput';
import PasswordInput from '../../../PasswordInput/PasswordInput';
import * as Styled from './ActivateForm.styled';
import Button from '../../../Button/Button';
import { post } from '../../../../util/request';
import { platform_base_url } from '../../../../util/config';
import { completeAuthentication } from '../../Authentication.utils';
import Feature from '../../../../util/feature';
import { STEPS, useAuthState } from '../../Authentication.context';
import { GET_USER_EMAIL } from './ActivateForm.gql';
import { LINKS } from './ActivateForm.utils';

const PASSWORD_RESET_REQUIRED = 498;
const ACCOUNT_ALREADY_ACTIVATED = 400;

const ActivateSchema = Yup.object().shape({
  first_name: Yup.string('Please enter your first name.').required('Required'),
  last_name: Yup.string('Please enter your last name.').required('Required'),
  password: Yup.string('Please enter a password.').required('Required')
});

const { SIGNUP } = STEPS;

/**
 * `ActivateForm` allows a guest user to activate their account
 */
const ActivateForm = ({ onComplete, onPasswordResetRequired, perks }) => {
  const client = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);
  const [queryString, setQueryString] = useState('');
  const [id, setId] = useState('');
  const { setCurrentStep } = useAuthState();

  const { data } = useQuery(GET_USER_EMAIL, {
    skip: !id,
    variables: { hashedEmail: id }
  });

  const email = data?.user?.email;

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    const hashedEmail = params.get('hashed_email');
    const timestamp = params.get('timestamp');
    const hashedTimestamp = params.get('hashed_timestamp');

    if (!(hashedEmail && timestamp && hashedTimestamp)) {
      setCurrentStep(SIGNUP);
    } else {
      setQueryString(
        `?hashed_email=${hashedEmail}&timestamp=${timestamp}&hashed_timestamp=${hashedTimestamp}`
      );
      setId(hashedEmail);
    }
  }, []);

  const handleSubmit = async (
    { first_name, last_name, password },
    { setErrors }
  ) => {
    setIsLoading(true);

    await post(
      `${platform_base_url}/v2/identity/activate${queryString}`,
      { first_name, last_name, password },
      { credentials: 'include' }
    )
      .then(completeAuthentication.bind(null, client))
      .then(onComplete.bind(null, { id, method: 'bombas' }))
      .catch((e) => {
        const error = e?.response?.errors?.[0];

        if (
          error?.status === PASSWORD_RESET_REQUIRED ||
          error?.status === ACCOUNT_ALREADY_ACTIVATED
        ) {
          post(`${platform_base_url}/v2/identity/forgot-password`, {
            email
          });
          onPasswordResetRequired(email);
        } else {
          setErrors({
            first_name: true,
            last_name: true,
            password: 'Could not activate account'
          });

          window.DD_RUM?.addError(e, { location: 'identity/activate' });
        }
        setIsLoading(false);
      });
  };

  return (
    <Styled.ActivateForm>
      <Styled.DescriptionText>
        Activate your account
        {perks && (
          <>
            {' '}
            for <Styled.Bold>{perks}</Styled.Bold> on your order
          </>
        )}
        .
      </Styled.DescriptionText>
      <Formik
        initialValues={{
          first_name: '',
          last_name: '',
          password: ''
        }}
        validationSchema={ActivateSchema}
        onSubmit={handleSubmit}
      >
        <Form>
          <Styled.FormWrap>
            <TextInput
              id="activate_first"
              label="First Name"
              name="first_name"
              autoComplete="name"
            />
            <TextInput
              id="activate_last"
              label="Last Name"
              name="last_name"
              autoComplete="name"
            />
            <PasswordInput
              id="login_pass"
              name="password"
              label="Password"
              autoComplete="password"
              withRequirements
            />
            <Button
              type="submit"
              variant="primary"
              isFull
              size="large"
              isLoading={isLoading}
            >
              Activate Account
            </Button>
          </Styled.FormWrap>
        </Form>
      </Formik>

      <Styled.DisclaimerText>
        {Feature.isEnabled('auto_email_marketing_opt_in') ? (
          <>
            By selecting &quot;Activate,&quot; you are giving us permission to
            include you in marketing emails. For additional information, please
            see our{' '}
          </>
        ) : (
          <>By selecting &quot;Activate,&quot; I agree to the Bombas </>
        )}{' '}
        {LINKS.termsOfUse}
        and {LINKS.privacyPolicy}. California consumers, see our Notice of
        Financial Incentives in Section 10 of our {LINKS.privacyPolicy}.
      </Styled.DisclaimerText>
    </Styled.ActivateForm>
  );
};

ActivateForm.propTypes = {
  /**
   * Callback on successful submission of sign up form
   */
  onComplete: PropTypes.func.isRequired,
  /**
   * Callback function to run when a password reset is required to authenticate
   */
  onPasswordResetRequired: PropTypes.func.isRequired,
  /**
   * Perk that is offered by activating your account and logging in
   */
  perks: PropTypes.string
};

ActivateForm.defaultProps = {
  perks: null
};

ActivateForm.displayName = 'ActivateForm';

export default ActivateForm;
